import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { Trash2, Plus, Users } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface AlphaTester {
  id: string;
  email: string;
  notes: string | null;
  created_at: string;
}

const AlphaTesters = () => {
  const [testers, setTesters] = useState<AlphaTester[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchTesters = async () => {
    const { data, error } = await supabase
      .from('alpha_testers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: 'Error loading testers',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      setTesters(data || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTesters();
  }, []);

  const handleAddTester = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      const { error } = await supabase
        .from('alpha_testers')
        .insert({
          email: newEmail.toLowerCase().trim(),
          notes: newNotes.trim() || null,
        });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: 'Email already approved',
            description: 'This email is already in the alpha testers list.',
            variant: 'destructive',
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: 'Tester added',
          description: `${newEmail} can now sign up for alpha testing.`,
        });
        setNewEmail('');
        setNewNotes('');
        fetchTesters();
      }
    } catch (error: any) {
      toast({
        title: 'Error adding tester',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteTester = async (id: string) => {
    const { error } = await supabase
      .from('alpha_testers')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: 'Error removing tester',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Tester removed',
        description: 'This email can no longer sign up.',
      });
      fetchTesters();
    }
    setDeleteId(null);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Alpha Testers</h1>
        <p className="text-muted-foreground">
          Manage who can sign up during alpha testing. Only approved emails can create accounts.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Add New Tester Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add New Tester
            </CardTitle>
            <CardDescription>
              Enter an email address to approve them for alpha testing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddTester} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tester@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="e.g., Shop owner from Portland, Friend from college..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  rows={2}
                />
              </div>
              <Button type="submit" disabled={isAdding}>
                {isAdding ? 'Adding...' : 'Add Tester'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Current Testers List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Approved Testers ({testers.length})
            </CardTitle>
            <CardDescription>
              These emails can sign up and use the alpha version
            </CardDescription>
          </CardHeader>
          <CardContent>
            {testers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No alpha testers added yet. Add your first tester above!
              </p>
            ) : (
              <div className="space-y-2">
                {testers.map((tester) => (
                  <div
                    key={tester.id}
                    className="flex items-start justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{tester.email}</p>
                      {tester.notes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {tester.notes}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Added {new Date(tester.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(tester.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Alpha Tester?</AlertDialogTitle>
            <AlertDialogDescription>
              This email will no longer be able to sign up for alpha testing.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDeleteTester(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AlphaTesters;
